import { useNavigate } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { Mic, Search, Smile, Plus } from "lucide-react";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";

export default function SearchBox({
  holder,
  url,
  showMic = false,
  actionType = "mic",
  buttonSize = 56,
  actionIconSize,
  variant = "default",
}) {
  const [isSticky, setIsSticky] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const searchRef = useRef(null);
  const placeholderRef = useRef(null); // For space when fixed
  const inputRef = useRef(null);
  const recognitionRef = useRef(null);
  const navigate = useNavigate();

  const handleSubmit = (e) => {
    e.preventDefault();
    if (query.trim()) {
      navigate(`/${url}?q=${encodeURIComponent(query.trim())}`);
    }
  };

  const [query, setQuery] = useState("");

  const handleMicClick = () => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;

    // Graceful fallback for browsers that do not support speech recognition.
    if (!SpeechRecognition) {
      inputRef.current?.focus();
      return;
    }

    if (isListening && recognitionRef.current) {
      recognitionRef.current.stop();
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.lang = "en-US";
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;

    recognition.onstart = () => setIsListening(true);
    recognition.onerror = () => setIsListening(false);
    recognition.onend = () => setIsListening(false);
    recognition.onresult = (event) => {
      const transcript = event.results?.[0]?.[0]?.transcript?.trim() || "";
      if (!transcript) return;

      setQuery(transcript);
      navigate(`/${url}?q=${encodeURIComponent(transcript)}`);
    };

    recognitionRef.current = recognition;
    recognition.start();
  };

  const handleActionClick = () => {
    if (actionType === "symptom") {
      navigate("/symptom-checker");
      return;
    }

    handleMicClick();
  };

  const resolvedIconSize = actionIconSize || (actionType === "symptom" ? 28 : 24);
  const actionAriaLabel = actionType === "symptom" ? "Open symptom checker" : "Voice search";
  const isComposerVariant = variant === "composer";
  const composerButtonSize = 64;
  const composerGap = 14;
  const composerFixedWidth = composerButtonSize * 3 + composerGap * 3;

  useEffect(() => {
    const handleScroll = () => {
      if (!searchRef.current || !placeholderRef.current) return;
      const rect = placeholderRef.current.getBoundingClientRect();

      if (rect.top <= 10) {
        setIsSticky(true);
      } else {
        setIsSticky(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
    };
  }, []);

  return (
    <form onSubmit={handleSubmit} style={{ width: "100%" }}>
      {/* Placeholder to prevent content jump when fixed */}
      <div
        ref={placeholderRef}
        style={{ height: isSticky ? searchRef.current?.offsetHeight : 0 }}
      />

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: isComposerVariant ? composerGap : (showMic ? 10 : 0),
          width: "100%",
        }}
      >
        {isComposerVariant ? (
          <button
            type="button"
            aria-label="Focus search"
            onClick={() => inputRef.current?.focus()}
            style={{
              width: composerButtonSize,
              height: composerButtonSize,
              borderRadius: 999,
              border: "2px solid rgba(255,255,255,0.7)",
              background: "rgba(255,255,255,0.08)",
              color: "#f4f4f5",
              display: "grid",
              placeItems: "center",
              cursor: "pointer",
              flexShrink: 0,
            }}
          >
            <Search size={26} />
          </button>
        ) : null}

        <motion.div
          ref={searchRef}
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          style={{
            position: isSticky ? "fixed" : "relative",
            top: isSticky ? 10 : "auto",
            width: isSticky
              ? (isComposerVariant
                  ? (placeholderRef.current?.offsetWidth || 0) - composerFixedWidth
                  : (showMic
                      ? (placeholderRef.current?.offsetWidth || 0) - 66
                      : placeholderRef.current?.offsetWidth))
              : "100%",
            maxWidth: isComposerVariant ? "none" : "600px",
            borderRadius: isComposerVariant ? "16px" : "20px",
            display: "flex",
            alignItems: "center",
            padding: isComposerVariant ? "14px 16px" : "14px 16px",
            border: isComposerVariant ? "1px solid rgba(255,255,255,0.06)" : "1px solid #e2e5ef",
            boxShadow: isComposerVariant ? "inset 0 1px 0 rgba(255,255,255,0.06)" : "0 2px 8px rgba(15,23,42,0.06)",
            background: isComposerVariant ? "linear-gradient(180deg, #2b2b2f 0%, #26262a 100%)" : "#ffffff",
            zIndex: 1000,
            left: isSticky ? placeholderRef.current?.getBoundingClientRect().left : "auto",
            flex: 1,
            minHeight: isComposerVariant ? 64 : "auto",
          }}
        >
          {!isComposerVariant ? <Search size={20} color="var(--primary)" /> : null}
          {isComposerVariant ? (
            <span
              aria-hidden="true"
              style={{
                width: 4,
                height: 42,
                borderRadius: 999,
                background: "#22c55e",
                display: "inline-block",
                flexShrink: 0,
              }}
            />
          ) : null}
          <input
            ref={inputRef}
            type="text"
            placeholder={holder}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="border-0 w-100"
            style={{
              border: "none",
              outline: "none",
              flex: 1,
              padding: isComposerVariant ? "0 14px" : "0 10px",
              background: "transparent",
              color: isComposerVariant ? "#f5f5f6" : "var(--text)",
              fontSize: isComposerVariant ? 17 : 15,
            }}
          />
          <button
            type="button"
            aria-label="Voice search"
            onClick={handleMicClick}
            style={{
              background: "none",
              border: "none",
              padding: "0 2px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              color: isListening ? "#dc2626" : (isComposerVariant ? "#a1a1aa" : "#9ca3af"),
              flexShrink: 0,
            }}
          >
            <Mic size={isComposerVariant ? 34 : 18} />
          </button>
        </motion.div>

        {isComposerVariant ? (
          <>
            <button
              type="button"
              aria-label="Emojis"
              onClick={() => inputRef.current?.focus()}
              style={{
                width: composerButtonSize,
                height: composerButtonSize,
                borderRadius: 999,
                border: "2px solid rgba(255,255,255,0.7)",
                background: "rgba(255,255,255,0.08)",
                color: "#f4f4f5",
                display: "grid",
                placeItems: "center",
                cursor: "pointer",
                flexShrink: 0,
              }}
            >
              <Smile size={34} />
            </button>

            <button
              type="button"
              aria-label={actionAriaLabel}
              onClick={handleActionClick}
              style={{
                width: composerButtonSize,
                height: composerButtonSize,
                borderRadius: 999,
                border: "2px solid rgba(255,255,255,0.7)",
                background: "rgba(255,255,255,0.08)",
                color: actionType === "mic" && isListening ? "#dc2626" : "#f4f4f5",
                display: "grid",
                placeItems: "center",
                cursor: "pointer",
                flexShrink: 0,
              }}
            >
              <Plus size={36} />
            </button>
          </>
        ) : null}

        {showMic && !isComposerVariant && (
          <button
            type="button"
            aria-label={actionAriaLabel}
            onClick={handleActionClick}
            style={{
              width: buttonSize,
              height: buttonSize,
              borderRadius: 18,
              border: "1px solid #e2e5ef",
              background: "#ffffff",
              color: actionType === "mic" && isListening ? "#dc2626" : "var(--primary)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 2px 8px rgba(15,23,42,0.08)",
              cursor: "pointer",
              flexShrink: 0,
            }}
          >
            {actionType === "symptom" ? <AutoAwesomeIcon style={{ fontSize: resolvedIconSize }} /> : <Mic size={resolvedIconSize} />}
          </button>
        )}
      </div>
    </form>
  );
}
